//-- Copyright (c) 2021 TMT Insights Group, LLC
import PolarisIcon from '@/components/_common/PolarisIcon';
import { Box, Chip, Fade, Grid, Typography, Tooltip, IconButton } from '@mui/material';
import { getUnsupportedPreviewFormats, insertWordBreaks, formatBytes } from '@/_utils/utils';
import ClickCopy from '@/components/_common/ClickCopy/ClickCopy';
import FileType from '@/components/_common/Icons/FileType';
import DateTimeDisplay from '@/components/_common/DateTimeDisplay';
import { LabelValue } from '@/components/_common';
import styled from '@emotion/styled';
import { useAppSelector } from '@/redux/hooks';
import { selectFileTypeGroupings } from '@/redux/selectors/constantsSelectors';
import { JobDetailInventoryFile } from '@/redux/slices/slice_types';
import { FileFormat } from '@/constants/constants';

type FilesAsListProps = {
  files: JobDetailInventoryFile[];
  copyDownloadLink: (file: JobDetailInventoryFile) => void;
  handleDownload: (file: JobDetailInventoryFile) => void;
  onPreviewFile: (file: JobDetailInventoryFile) => void;
  copying: string | null;
  downloading: string | null;
  preparingPreview?: {
    name: string;
    url?: string;
  };
};

const FilesAsList = ({ files, copyDownloadLink, handleDownload, copying, downloading, preparingPreview, onPreviewFile }: FilesAsListProps) => {
  const fileTypeGroupings = useAppSelector(selectFileTypeGroupings);
  const disabledFileTypes = getUnsupportedPreviewFormats(fileTypeGroupings.other);

  if (!files.length) return null;
  return (
    <Wrapper>
      {!!files.length &&
        files.map((file, index) => {
          const id = `${file.name}_${index}`;
          const ext = file.file_type.startsWith('.') ? file.file_type.substring(1, file.file_type.length) : file.file_type;
          const cantPreview = disabledFileTypes.includes(ext as FileFormat);

          return (
            <Fade key={id} in style={{ transformOrigin: 'center top' }} timeout={(index + 1) * 200}>
              <ListItemGrid>
                <FileNameGrid item xs={12} md={6} lg={6}>
                  <Grid container alignItems="center" flexWrap="nowrap">
                    <FileType type={file.file_type} height={42} style={{ marginRight: 18 }} />
                    <div>
                      <ClickCopy copyText={file.name} placement="bottom">
                        <FileNameTypography variant="h3">{insertWordBreaks(file.name)}</FileNameTypography>
                      </ClickCopy>

                      <Grid container alignItems="center" sx={{ mt: 0.2 }}>
                        <DateTimeDisplay singleLine style={{ fontSize: '.9rem', opacity: 0.8, lineHeight: 1.2 }}>
                          {file.last_modified}
                        </DateTimeDisplay>
                        <Typography variant="caption" sx={{ ml: 1, pl: 1, borderLeft: '1px solid', lineHeight: 1.2 }}>
                          {formatBytes(file.size)}
                        </Typography>
                      </Grid>
                    </div>
                  </Grid>
                </FileNameGrid>

                <Grid item xs={12} md={6} lg={2} display={{ xs: 'none', lg: 'block' }}>
                  <LabelValue label="Source">{file.source_name}</LabelValue>
                </Grid>
                <FileActionsGrid item xs={12} md={6} lg={3} alignItems="center" flexWrap="nowrap">
                  <div style={{ marginRight: 10 }}>
                    {!!file.label && <Chip label={insertWordBreaks(file.label)} variant="outlined" color="info" />}
                    <Box display={{ xs: 'block', md: 'block', lg: 'none' }}>
                      <LabelValue label="Source" sx={{ mt: 1 }}>
                        {file.source_name}
                      </LabelValue>
                    </Box>
                  </div>
                  <Tooltip title="Copy Download Link" placement="top">
                    <IconButton size="large" color="default" aria-label="copy" onClick={() => copyDownloadLink(file)} sx={{ opacity: 0.5 }}>
                      <PolarisIcon icon={file.label === copying ? 'loading' : 'link'} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Download File" placement="top">
                    <IconButton size="large" color="primary" aria-label="download" onClick={() => handleDownload(file)}>
                      <PolarisIcon icon={file.name === downloading ? 'loading' : 'download'} />
                    </IconButton>
                  </Tooltip>
                  {cantPreview ? (
                    <Tooltip title="Preview is not supported for this file type">
                      <EyesClosedIconButton color="default">
                        <PolarisIcon icon="hidden" style={{ opacity: 0.3 }} />
                      </EyesClosedIconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Preview" placement="top">
                      <IconButton
                        disabled={cantPreview}
                        edge="end"
                        size="large"
                        color="primary"
                        aria-label="preview file"
                        onClick={() => onPreviewFile(file)}
                      >
                        <PolarisIcon icon={file.name === preparingPreview?.name && !preparingPreview.url ? 'loading' : 'preview'} />
                      </IconButton>
                    </Tooltip>
                  )}
                </FileActionsGrid>
              </ListItemGrid>
            </Fade>
          );
        })}
    </Wrapper>
  );
};

export default FilesAsList;

const Wrapper = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
`;

const ListItemGrid = styled(Grid)`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing(4)};

  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.palette.divider};
  }
`;

const FileNameGrid = styled(Grid)`
  display: flex;
  justify-content: center;
  flex-direction: column;
`;

const FileNameTypography = styled(Typography)`
  font-size: 1rem;
  line-height: 1.3;
  word-break: break-word;
  height: 100%;
  display: flex;
  align-items: center;
`;

const FileActionsGrid = styled(Grid)`
  display: flex;
  justify-content: flex-end;

  & .MuiTypography-subtitle2 {
    opacity: 0.7;
  }

  & .MuiChip-root {
    padding: ${({ theme }) => theme.spacing(0.75, 0.5)};
    text-align: center;
    height: auto;
  }

  & .MuiChip-label {
    text-transform: uppercase;
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;

const EyesClosedIconButton = styled(IconButton)`
  color: ${({ theme }) => theme.palette.text.primary};
  opacity: 0.6;
`;
